import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, X, Clock, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * UpcomingMeetingBanner
 * Shows a soft, dismissible card at the top of the dashboard whenever an
 * upcoming calendar event (next 48h) matches a saved person card.
 * Helps user remember names + memory hooks before the meeting.
 *
 * Dismissals are stored in localStorage per "match key" so it doesn't
 * keep nagging once read.
 */
export default function UpcomingMeetingBanner() {
  const [matches, setMatches] = useState([]);
  const [idx, setIdx] = useState(0);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('umb_dismissed') || '{}'); } catch { return {}; }
  });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/social/upcoming-suggestions?hours=48`, { withCredentials: true });
      setMatches(data.matches || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000);  // refresh every 5 min
    return () => clearInterval(t);
  }, [load]);

  // Filter out dismissed
  const active = matches.filter((m) => {
    const key = `${m.event.id || m.event.starts_at}_${m.person.id}`;
    return !dismissed[key];
  });

  if (active.length === 0) return null;
  const safeIdx = Math.min(idx, active.length - 1);
  const m = active[safeIdx];
  if (!m) return null;

  const dismissKey = `${m.event.id || m.event.starts_at}_${m.person.id}`;

  const dismiss = () => {
    const next = { ...dismissed, [dismissKey]: Date.now() };
    setDismissed(next);
    try { localStorage.setItem('umb_dismissed', JSON.stringify(next)); } catch { /* ignore */ }
    if (idx >= active.length - 1) setIdx(0);
  };

  const startsDate = new Date(m.event.starts_at);
  const now = new Date();
  const diffMs = startsDate.getTime() - now.getTime();
  const hours = Math.floor(diffMs / 3600000);
  const minutes = Math.floor((diffMs % 3600000) / 60000);
  const whenLabel =
    hours >= 24 ? `om ${Math.floor(hours / 24)} dag${Math.floor(hours / 24) > 1 ? 'ar' : ''}` :
    hours >= 1 ? `om ${hours} h ${minutes > 0 ? minutes + ' min' : ''}`.trim() :
    minutes > 0 ? `om ${minutes} min` :
    'snart';

  const dateLabel = startsDate.toLocaleString('sv-SE', {
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div
      className="neo-card p-3 mb-4 bg-gradient-to-br from-[#FFE8F5] via-[#F5EEFF] to-[#E8F0FF]"
      data-testid="upcoming-meeting-banner"
    >
      <div className="flex items-start gap-2">
        <div className="w-10 h-10 rounded-full border-2 border-black bg-[#FFC8DD] flex items-center justify-center text-base font-black shrink-0">
          {m.person.name.slice(0, 2).toUpperCase()}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-60 flex items-center gap-1">
                <Users className="w-3 h-3" /> Memory-hint · {whenLabel}
              </p>
              <h3 className="font-black text-sm leading-tight" data-testid="umb-event-title">
                {m.event.title}
              </h3>
              <p className="text-[11px] opacity-70 flex items-center gap-1.5 mt-0.5">
                <Clock className="w-3 h-3" /> {dateLabel}
                {m.event.location && (
                  <>
                    <span className="opacity-40">·</span>
                    <MapPin className="w-3 h-3" /> {m.event.location}
                  </>
                )}
              </p>
            </div>
            <button
              onClick={dismiss}
              className="neo-btn p-1 bg-white shrink-0"
              data-testid="umb-dismiss"
              title="Stäng"
            >
              <X className="w-3 h-3" />
            </button>
          </div>

          {/* Person card preview */}
          <div className="mt-2 border-2 border-black rounded-xl p-2 bg-white" data-testid="umb-person-preview">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="font-bold text-sm">{m.person.name}</span>
              {m.person.relation && (
                <span className="text-[10px] bg-[#FFE8A3] border border-black rounded-full px-1.5 py-0.5">
                  {m.person.relation}
                </span>
              )}
            </div>
            {(m.person.hooks || []).length > 0 && (
              <ul className="space-y-0.5 mb-1">
                {m.person.hooks.slice(0, 5).map((h, i) => (
                  <li key={i} className="text-[11px] flex gap-1.5">
                    <span className="opacity-50">•</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            )}
            {m.person.notes && (
              <p className="text-[10px] italic opacity-70 leading-snug border-t border-black/10 pt-1">
                {m.person.notes}
              </p>
            )}
          </div>

          <p className="text-[10px] opacity-60 italic mt-1.5">
            💡 Tips: börja med "Hur är det med {(m.person.hooks?.[0] || 'dig').replace(/^(barn |hund |katt |projekt )/i, '')}?" — visar att du minns.
          </p>
        </div>
      </div>

      {/* Pagination dots if multiple */}
      {active.length > 1 && (
        <div className="flex items-center justify-center gap-2 mt-2 pt-2 border-t border-black/10">
          <button
            onClick={() => setIdx(Math.max(0, safeIdx - 1))}
            disabled={safeIdx === 0}
            className="neo-btn p-1 bg-white disabled:opacity-40"
            data-testid="umb-prev"
          >
            <ChevronLeft className="w-3 h-3" />
          </button>
          <span className="text-[10px] font-bold opacity-60">
            {safeIdx + 1} / {active.length}
          </span>
          <button
            onClick={() => setIdx(Math.min(active.length - 1, safeIdx + 1))}
            disabled={safeIdx >= active.length - 1}
            className="neo-btn p-1 bg-white disabled:opacity-40"
            data-testid="umb-next"
          >
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}
